<template>
  <VueFinalModal
    :click-to-close="modalAllowsDismiss"
    :esc-to-close="modalAllowsDismiss"
    :focus-trap="modalFocusTrap"
    class="final-modal"
    @closed="handleModalClosed"
    overlay-transition="vfm-fade"
    :class="{
      'theme--default': theme === 'light',
      'theme--dark': theme === 'dark',
    }"
  >
    <template
      #default="{
        // @ts-ignore - vue-final-modal exposes `close` through slot
        close,
      }"
    >
      <div
        class="content create-org-modal"
        :class="{
          'create-org-modal--wide': wizardStep >= 3,
          'create-org-modal--creating': wizardStep === 2,
        }"
        aria-modal="true"
        role="alertdialog"
        aria-labelledby="modalTitle"
        tabindex="-1"
      >
        <div class="header">
          <div id="modalTitle" class="title">
            <h4 class="text">
              <span>{{ title }}</span>
              <span
                v-if="wizardStep >= 1"
                class="step-indicator"
                aria-hidden="true"
              >
                {{ userFacingStep }}/{{ USER_FACING_WIZARD_STEPS }}
              </span>
            </h4>
          </div>
          <BaseButton
            v-if="showHeaderClose"
            kind="auxiliary"
            class="close-icon"
            @click="onHeaderClose(close)"
          >
            <template #icon>
              <CloseIcon />
            </template>
          </BaseButton>
        </div>

        <!-- Loading existing org (skip name / creation steps) -->
        <template v-if="existingOrgBootstrapPending">
          <div class="body purpose-body">
            <section class="setup-section">
              <div class="plan-phase-loading plan-phase-loading--standalone">
                <div class="plan-phase-loading-spin-host">
                  <div
                    class="creating-loader plan-phase-loader"
                    role="status"
                    aria-live="polite"
                    aria-label="Loading organization"
                  >
                    <span class="creating-spinner" />
                  </div>
                </div>
                <div
                  class="setup-question-hint plan-hint plan-preparing-hint plan-phase-loading-status"
                >
                  Loading organization…
                </div>
              </div>

              <p v-if="errorMessage" class="error creating-error">
                {{ errorMessage }}
              </p>
            </section>
          </div>

          <div class="footer">
            <div class="buttons">
              <BaseButton
                kind="main"
                type="tertiary"
                text="Cancel"
                @click="close"
              />
            </div>
          </div>
        </template>

        <!-- Step 1: name -->
        <template v-else-if="wizardStep === 1">
          <div class="body">
            <p style="margin-bottom: 16px">
              Enter a name for your new organization:
            </p>
            <FormInput
              ref="inputRef"
              :value="organizationName"
              type="text"
              placeholder="Organization name"
              state="default"
              @update:value="(value) => (organizationName = value)"
              @keydown="onKeydown($event, close)"
            />
          </div>

          <div class="footer">
            <div class="buttons">
              <BaseButton
                kind="main"
                type="tertiary"
                text="Cancel"
                @click="close"
              />
              <BaseButton
                kind="main"
                type="primary"
                :text="createButtonText"
                :state="createButtonState"
                @click="onCreate(close)"
              />
            </div>
          </div>
        </template>

        <!-- Step 2: creating org (same block layout as step 4 “Preparing plan”) -->
        <template v-else-if="wizardStep === 2">
          <div class="body purpose-body">
            <section class="setup-section">
              <div class="plan-phase-loading plan-phase-loading--standalone">
                <div class="plan-phase-loading-spin-host">
                  <div
                    class="creating-loader plan-phase-loader"
                    role="status"
                    aria-live="polite"
                    aria-label="Creating organization"
                  >
                    <span class="creating-spinner" />
                  </div>
                </div>
              </div>

              <p v-if="errorMessage" class="error creating-error">
                {{ errorMessage }}
              </p>
            </section>
          </div>
        </template>

        <!-- Steps 3–7: post–name flow -->
        <template v-else>
          <div class="body purpose-body">
            <!-- Step 3: use cases -->
            <section v-show="wizardStep === 3" class="setup-section">
              <p class="setup-question-hint">
                Your organization will be customized accordingly.
              </p>
              <div class="use-case-pills use-case-pills--multiline">
                <button
                  v-for="opt in primaryUseCases"
                  :key="opt.key"
                  type="button"
                  class="use-case-pill use-case-pill--multiline"
                  :class="{
                    'use-case-pill--selected': primaryUseCaseKeys.includes(
                      opt.key
                    ),
                  }"
                  @click="togglePrimaryUseCase(opt.key)"
                >
                  <span class="use-case-pill-icon" aria-hidden="true">
                    <component :is="opt.icon" />
                  </span>
                  <span class="use-case-pill-text">
                    <span class="use-case-pill-title">{{ opt.title }}</span>
                    <span class="use-case-pill-hint">{{ opt.hint }}</span>
                  </span>
                </button>
              </div>
            </section>

            <!-- Step 4: preparing plan -->
            <section v-show="wizardStep === 4" class="setup-section">
              <div class="plan-phase-loading plan-phase-loading--standalone">
                <div class="plan-phase-loading-spin-host">
                  <div
                    class="creating-loader plan-phase-loader"
                    role="status"
                    aria-live="polite"
                    aria-label="Preparing plan"
                  >
                    <span class="creating-spinner" />
                  </div>
                </div>
              </div>
            </section>

            <!-- Steps 5–6: plan review / executing setup -->
            <section
              v-show="wizardStep === 5 || wizardStep === 6"
              class="setup-section"
            >
              <div class="plan-header">
                <p class="setup-question-hint plan-hint">
                  The following operations will be carried out to set up your
                  organization.
                </p>

                <div
                  v-if="organizationPlanGroups.length"
                  class="plan-groups"
                  :aria-busy="isFinishing"
                >
                  <div
                    v-for="(group, gIdx) in organizationPlanGroups"
                    :key="group.key"
                    class="plan-uc"
                  >
                    <button
                      type="button"
                      class="plan-uc-toggle"
                      :aria-expanded="planGroupExpanded[gIdx]"
                      @click="togglePlanGroup(gIdx)"
                    >
                      <span
                        class="plan-uc-chevron"
                        :class="{
                          'plan-uc-chevron--open': planGroupExpanded[gIdx],
                        }"
                        aria-hidden="true"
                        >›</span
                      >
                      <span class="plan-uc-heading">
                        <span class="plan-uc-title">{{ group.title }}</span>
                        <span
                          v-if="isPlanUseCaseLoading(gIdx)"
                          class="plan-uc-status plan-uc-status--loading"
                          role="status"
                          aria-live="polite"
                          aria-label="Setting up this use case"
                        >
                          <span class="plan-uc-spinner" />
                        </span>
                        <span
                          v-else-if="isPlanUseCaseComplete(gIdx)"
                          class="plan-uc-done"
                          role="img"
                          aria-label="This use case is fully set up"
                        >
                          <svg
                            class="plan-uc-done-icon"
                            width="9"
                            height="9"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M2.5 6L5 8.5L9.5 3.5"
                              stroke="currentColor"
                              stroke-width="1.35"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </span>
                      </span>
                    </button>
                    <ul
                      v-show="planGroupExpanded[gIdx]"
                      class="plan-checklist plan-checklist--nested"
                    >
                      <li
                        v-for="(op, localIdx) in group.operations"
                        :key="`${group.key}-${localIdx}`"
                        class="plan-checklist-item"
                      >
                        <span
                          class="plan-check"
                          :class="{
                            'plan-check--done': isPlanStepCompleted(
                              globalPlanOpIndex(gIdx, localIdx)
                            ),
                          }"
                          aria-hidden="true"
                        >
                          <TickIcon />
                        </span>
                        <span
                          class="plan-check-text"
                          :class="{
                            'plan-check-text--done': isPlanStepCompleted(
                              globalPlanOpIndex(gIdx, localIdx)
                            ),
                          }"
                          >{{ op }}</span
                        >
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <!-- Step 7: complete -->
            <section
              v-show="wizardStep === 7"
              class="setup-section complete-section"
            >
              <p
                class="setup-question complete-title"
                role="heading"
                aria-level="2"
              >
                Your organization is ready! 🎉
              </p>
            </section>

            <p v-if="purposeError" class="error setup-error">
              {{ purposeError }}
            </p>
          </div>

          <div
            v-if="wizardStep !== 2 && wizardStep !== 4 && wizardStep !== 7"
            class="footer"
          >
            <div
              v-if="wizardStep === 3"
              class="buttons footer-btns plan-footer"
            >
              <div class="plan-footer-actions">
                <BaseButton
                  kind="main"
                  type="tertiary"
                  text="Skip"
                  tooltip="Let me set it up myself"
                  tooltip-display="always"
                  @click="onSkipManualSetup(close)"
                />
                <BaseButton
                  kind="main"
                  type="primary"
                  :text="purposeContinueLabel"
                  :state="purposeContinueState"
                  @click="onContinuePurposeStep(close)"
                />
              </div>
            </div>
            <div
              v-if="wizardStep === 5 || wizardStep === 6"
              class="buttons footer-btns plan-footer"
            >
              <BaseButton
                kind="main"
                type="tertiary"
                text="Back"
                :state="purposeFooterBackState"
                @click="onBackPurposeStep(close)"
              />
              <div class="plan-footer-actions">
                <BaseButton
                  kind="main"
                  type="tertiary"
                  text="Skip"
                  tooltip="Let me set it up myself"
                  tooltip-display="always"
                  :state="planActionButtonState"
                  @click="onSkipManualSetup(close)"
                />
                <BaseButton
                  v-if="wizardStep === 5"
                  kind="auxiliary"
                  type="tertiary"
                  text="Edit plan"
                  tooltip="Edit the plan with AI"
                  tooltip-display="always"
                  class="plan-edit-btn"
                  :state="planActionButtonState"
                  @click="onEditPlanWithAgent(close)"
                >
                </BaseButton>
                <BaseButton
                  kind="main"
                  type="primary"
                  text="Set up"
                  :state="setUpButtonState"
                  @click="onExecutePlannedSetup(close)"
                />
              </div>
            </div>
          </div>

          <div v-if="wizardStep === 7" class="footer">
            <div class="buttons footer-btns complete-footer">
              <BaseButton
                kind="main"
                type="primary"
                text="Open"
                @click="onOpenOrganization(close)"
              />
            </div>
          </div>
        </template>
      </div>
    </template>
  </VueFinalModal>
</template>

<script lang="ts">
import startCase from "lodash/startCase";
import type { Component } from "vue";
import {
  computed,
  defineComponent,
  markRaw,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
  type PropType,
} from "vue";
import { VueFinalModal } from "vue-final-modal";
import { useRouter } from "vue-router";

import type {
  GraphDatabaseDictionary,
  MenuItemAppScreen,
  OrgId,
  TableId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import { pendingMessage } from "@/components/ai/pendingMessage";
import BaseButton from "@/components/base/BaseButton.vue";
import schema from "@/components/editor/prosemirror/schema";
import { serializeDoc } from "@/components/editor/serialize";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import BillingIcon from "@/components/icons/BillingIcon.vue";
import BoardIcon from "@/components/icons/BoardIcon.vue";
import BusinessPersonIcon from "@/components/icons/BusinessPersonIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import FeedbackIcon from "@/components/icons/FeedbackIcon.vue";
import NewsIcon from "@/components/icons/NewsIcon.vue";
import PageIcon from "@/components/icons/PageIcon.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import PlanClipboardIcon from "@/components/icons/PlanClipboardIcon.vue";
import TickIcon from "@/components/icons/TickIcon.vue";
import * as inject from "@/components/inject";
import {
  importPackage,
  type InstallDatabaseMode,
} from "@/components/marketplace/import";
import { getDataName, loadPackage } from "@/components/marketplace/package";
import { eachPackagedObject } from "@/components/marketplace/traverse";
import type {
  Package,
  PackagedObject,
  PackagedRoot,
} from "@/components/marketplace/types";
import { typeDiff } from "@/components/marketplace/utils";
import { applyViewConfigurations } from "@/components/marketplace/view-configurations";
import {
  ORGANIZATION_APP_SCREEN_IDS,
  ORGANIZATION_APP_SCREEN_LABELS,
} from "@/components/menu/menuItems";
import { modalFocusTrap } from "@/components/modals/focus-trap";
import { createOrganization } from "@/components/organizations";
import {
  ensureProvisionedDatabases,
  ensureWorkspaceHome,
  getOrganizationDefaultWorkspace,
} from "@/components/workspaces";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { RouteNames } from "@/routes/types";
import {
  navigateToAgentConversation,
  navigateToObject,
} from "@/utils/navigate";
import { isRecord } from "@/utils/unknown";

type Phase = "name" | "creating" | "purpose";

/** Shown in header: 1 name → 2 use cases → 3 plan (internal wizardStep 1–7 maps into these). */
const USER_FACING_WIZARD_STEPS = 3;
const CREATION_MIN_DISPLAY_MS = 1000;

type PrimaryUseCaseKey =
  | "projects"
  | "knowledge"
  | "recruiting"
  | "marketing"
  | "finance"
  | "success"
  | "sales";

/**
 * How a use case is fulfilled — exactly one of:
 * - `extensionId`: a Contentful marketplace starter to install (empty when
 *   the starter is not published yet);
 * - `screenId`: an app screen provided out of the box by the
 *   organization's provisioned types. Selecting it installs nothing, it only
 *   pins the screen to the menu (deselecting unpins it).
 */
type PrimaryUseCaseSetup =
  | { extensionId: string; screenId?: undefined }
  | { screenId: MenuItemAppScreen; extensionId?: undefined };

/** Single source of truth: UI label, hint, icon, and setup. */
const PRIMARY_USE_CASE_DEFINITIONS: ReadonlyArray<
  {
    key: PrimaryUseCaseKey;
    title: string;
    hint: string;
    icon: Component;
  } & PrimaryUseCaseSetup
> = [
  {
    key: "projects",
    title: "Projects",
    hint: "projects, tickets, epics…",
    icon: markRaw(BoardIcon),
    screenId: ORGANIZATION_APP_SCREEN_IDS.Projects,
  },
  {
    key: "knowledge",
    title: "Knowledge",
    hint: "pages, meetings, verifications…",
    icon: markRaw(PageIcon),
    screenId: ORGANIZATION_APP_SCREEN_IDS.Pages,
  },
  {
    key: "sales",
    title: "Sales (CRM)",
    hint: "accounts, people, deals, orders…",
    icon: markRaw(BusinessPersonIcon),
    extensionId: "7sVnGKFRvNeqIh9NKICpzL",
  },
  {
    key: "recruiting",
    title: "Recruiting (ATS)",
    hint: "openings, candidates…",
    icon: markRaw(PersonIcon),
    extensionId: "5drwzLXPdrlWLEk5iZea2R",
  },
  {
    key: "finance",
    title: "Finance",
    hint: "fund raising, investors, expense tracking…",
    icon: markRaw(BillingIcon),
    extensionId: "0p1XHt5Gzi9C6jGRm28rM",
  },
  {
    key: "success",
    title: "Success",
    hint: "roadmap, feedback, bug reports, feature requests…",
    icon: markRaw(FeedbackIcon),
    extensionId: "554NbNzjoNpZK53yRNrLC3",
  },
  {
    key: "marketing",
    title: "Marketing",
    hint: "campaigns, content calendar, initiatives…",
    icon: markRaw(NewsIcon),
    extensionId: "1fToYWGcPc4PAFomLZFL32",
  },
];

const PRIMARY_USE_CASES = PRIMARY_USE_CASE_DEFINITIONS.map(
  ({ key, title, hint, icon }) => ({ key, title, hint, icon })
);

/** Built-in use cases are preselected: they cost nothing to keep. */
const DEFAULT_PRIMARY_USE_CASE_KEYS: readonly PrimaryUseCaseKey[] =
  PRIMARY_USE_CASE_DEFINITIONS.filter((definition) => definition.screenId).map(
    (definition) => definition.key
  );

const onboardingExtensionId = (key: PrimaryUseCaseKey): string | undefined =>
  PRIMARY_USE_CASE_DEFINITIONS.find((definition) => definition.key === key)
    ?.extensionId;

const screenIdForUseCase = (
  key: PrimaryUseCaseKey
): MenuItemAppScreen | undefined =>
  PRIMARY_USE_CASE_DEFINITIONS.find((definition) => definition.key === key)
    ?.screenId;

type PlannedPackage = {
  key: PrimaryUseCaseKey;
  pkg: Package;
};

type OrganizationPlanGroup = {
  key: PrimaryUseCaseKey;
  title: string;
  operations: string[];
};

const formatPlanByUseCaseForAssist = (
  groups: OrganizationPlanGroup[]
): string =>
  groups
    .map((g) => {
      const body = g.operations.map((op, i) => `  ${i + 1}. ${op}`).join("\n");
      return `${g.title}:\n${body}`;
    })
    .join("\n\n");

const packageDatabaseName = (dbId: string, pkg: Package): string => {
  const schema = pkg.databases?.[dbId]?.schema;
  if (!isRecord(schema)) return dbId;
  const name = schema["name"];
  if (typeof name === "string" && name.trim()) {
    return name.trim();
  }
  return dbId;
};

const objectName = (obj: PackagedObject): string => {
  const rec = isRecord(obj.meta.properties) ? obj.meta.properties : null;
  if (rec) {
    const preferred = ["Title", "title", "Name", "name"];
    for (const key of preferred) {
      const value = rec[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    for (const value of Object.values(rec)) {
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }
  return obj.meta.rowId || "Object";
};

/**
 * When importing several onboarding packages, later packages often redefine the same logical types
 * (e.g. Page). Match workspace databases by `data_name` and use `extend` / `use` instead of `create`.
 */
function buildOnboardingInstallMaps(
  pkg: Package,
  workspaceDict: GraphDatabaseDictionary
): {
  modeByDbId: Record<string, InstallDatabaseMode | undefined>;
  targetByDbId: Record<string, TableId | undefined>;
} {
  const normalize = (s: string) => s.trim().toLowerCase();

  const byDataName = new Map<string, TableId[]>();
  for (const db of Object.values(workspaceDict)) {
    const dn =
      typeof db.data_name === "string" && db.data_name.trim()
        ? db.data_name.trim()
        : "";
    if (!dn) continue;
    const key = normalize(dn);
    const arr = byDataName.get(key) ?? [];
    arr.push(db.id);
    byDataName.set(key, arr);
  }

  const modeByDbId: Record<string, InstallDatabaseMode | undefined> = {};
  const targetByDbId: Record<string, TableId | undefined> = {};

  for (const packagedId of Object.keys(pkg.databases ?? {})) {
    const packagedSchema = pkg.databases?.[packagedId]?.schema ?? null;

    const existingByPackagedId = workspaceDict[packagedId];
    if (existingByPackagedId) {
      const diff = typeDiff({
        packagedSchema,
        workspaceSchema: existingByPackagedId,
      });
      modeByDbId[packagedId] = diff.some((p) => p.willAdd) ? "extend" : "use";
      targetByDbId[packagedId] = packagedId;
      continue;
    }

    const typeName = getDataName(packagedSchema);
    if (!typeName) {
      modeByDbId[packagedId] = "create";
      continue;
    }

    const candidates = byDataName.get(normalize(typeName)) ?? [];
    if (candidates.length === 1) {
      const targetId = candidates[0];
      const workspaceDb = workspaceDict[String(targetId)];
      if (workspaceDb) {
        const diff = typeDiff({
          packagedSchema,
          workspaceSchema: workspaceDb,
        });
        modeByDbId[packagedId] = diff.some((p) => p.willAdd) ? "extend" : "use";
        targetByDbId[packagedId] = targetId;
        continue;
      }
    }

    modeByDbId[packagedId] = "create";
  }

  return { modeByDbId, targetByDbId };
}

const collectPackagedObjects = (root: PackagedRoot): PackagedObject[] => {
  const out: PackagedObject[] = [];
  eachPackagedObject(root, (obj) => out.push(obj));
  return out;
};

const buildOperationsFromPackage = (pkg: Package): string[] => {
  const operations: string[] = [];
  for (const dbId of Object.keys(pkg.databases ?? {})) {
    const dbName = startCase(packageDatabaseName(dbId, pkg));
    operations.push(`Create database ${dbName}`);
    const rowCount = pkg.databases?.[dbId]?.rows?.length ?? 0;
    if (rowCount > 0) {
      operations.push(`Populate ${dbName} with ${rowCount} objects`);
    }
  }
  for (const obj of collectPackagedObjects(pkg.root)) {
    operations.push(`Create object ${objectName(obj)}`);
  }
  return operations;
};

function formatPrimaryUseCasesInline(keys: PrimaryUseCaseKey[]): string {
  return keys
    .map(
      (k) => PRIMARY_USE_CASE_DEFINITIONS.find((x) => x.key === k)?.title ?? k
    )
    .join(", ");
}

export default defineComponent({
  name: "CreateOrganizationModal",
  components: {
    BaseButton,
    BillingIcon,
    BoardIcon,
    CloseIcon,
    EditIcon,
    FeedbackIcon,
    FormInput,
    NewsIcon,
    PageIcon,
    PersonIcon,
    PlanClipboardIcon,
    TickIcon,
    VueFinalModal,
  },
  props: {
    /** When set, skip name + creation steps — org already exists (e.g. post team onboarding). */
    existingOrganizationId: {
      type: String as PropType<OrgId | undefined>,
      default: undefined,
    },
    /** Called after the modal close animation completes. */
    afterClosed: {
      type: Function as PropType<(() => void) | undefined>,
      default: undefined,
    },
  },
  setup(props) {
    const theme = inject.theme();
    const profile = inject.profile();
    const browserStore = useBrowserStore(profile.value.user.accountId);
    const controller = inject.controller();
    const logger = inject.logger("CreateOrganizationModal");
    const baseLogger = logger.base();
    const menu = inject.menu();
    const router = useRouter();

    const workspaceDbDictForInstall = async (
      wsId: WorkspaceId
    ): Promise<GraphDatabaseDictionary> => {
      const ref = await controller.graph.database.list();
      const all = ref.release();
      return all?.[wsId] ?? {};
    };

    const phase = ref<Phase>("name");
    /** 1 name → 2 creating → 3 setup intro → 4 use cases → … → 8 done */
    const wizardStep = ref(1);
    /** True while resolving default workspace for {@link props.existingOrganizationId}. */
    const existingOrgBootstrapPending = ref(false);
    /** When set, skip auto-focus on the name field (existing-org bootstrap from queue/modal attrs). */
    const skipNameStepAutoFocus = ref(false);
    const organizationName = ref("");
    const inputRef = ref<InstanceType<typeof FormInput>>();

    const isBusy = ref(false);
    const errorMessage = ref<string | null>(null);

    const primaryUseCaseKeys = ref<PrimaryUseCaseKey[]>([
      ...DEFAULT_PRIMARY_USE_CASE_KEYS,
    ]);
    const purposeError = ref<string | null>(null);

    const createdOrgId = ref<OrgId | null>(null);
    const createdWorkspaceId = ref<WorkspaceId | null>(null);

    type WorkspaceInit = { homeObjectId: string };
    const workspaceInit = ref<WorkspaceInit | null>(null);
    const plannedPackages = shallowRef<PlannedPackage[]>([]);
    const organizationPlanGroups = ref<OrganizationPlanGroup[]>([]);
    /** One flag per entry in `organizationPlanGroups`; whether that section’s steps are visible. */
    const planGroupExpanded = ref<boolean[]>([]);
    const organizationSetupOperations = computed(() =>
      organizationPlanGroups.value.flatMap((g) => g.operations)
    );
    /** Flat end index of each `organizationPlanGroups` entry in the checklist. */
    const planGroupEndIndices = ref<number[]>([]);
    const isPlanning = ref(false);
    const isFinishing = ref(false);
    const lastCompletedPlanStepIndex = ref(-1);
    /** Index into `organizationPlanGroups` while that group is executing (step 6). */
    const installingPlanGroupIndex = ref<number | null>(null);

    const primaryUseCases = PRIMARY_USE_CASES;

    const trimmedNameForDisplay = computed(() => organizationName.value.trim());

    const userFacingStep = computed(() => {
      const s = wizardStep.value;
      if (s <= 2) return 1;
      if (s <= 4) return 2;
      return 3;
    });

    const modalAllowsDismiss = computed(
      () =>
        !isBusy.value &&
        !isPlanning.value &&
        !isFinishing.value &&
        wizardStep.value !== 7
    );

    const showHeaderClose = modalAllowsDismiss;

    watch(wizardStep, (step) => {
      if (step === 1) phase.value = "name";
      else if (step === 2) phase.value = "creating";
      else phase.value = "purpose";
    });

    const purposeContinueLabel = computed(() => "Continue");

    const title = computed(() => {
      if (wizardStep.value === 7) return "Ready";
      if (wizardStep.value === 5 || wizardStep.value === 6) return "Plan";
      if (wizardStep.value === 4) return "Preparing plan…";
      if (wizardStep.value === 3)
        return "What are the primary use cases for this organization?";
      if (wizardStep.value === 2) return "Creating organization…";
      return "Create organization";
    });

    const createButtonState = computed(() => {
      if (isBusy.value) return "loading";
      return trimmedNameForDisplay.value ? "default" : "disabled";
    });

    const createButtonText = computed(() => {
      if (isBusy.value) return "Creating…";
      return "Continue";
    });

    const purposeContinueState = computed(() => {
      if (isPlanning.value) return "loading" as const;
      if (wizardStep.value !== 3) return "disabled" as const;
      return primaryUseCaseKeys.value.length > 0 ? "default" : "disabled";
    });

    const purposeFooterBackState = computed(() =>
      isFinishing.value || isPlanning.value ? "disabled" : "default"
    );

    const planActionButtonState = computed(() =>
      isPlanning.value || isFinishing.value ? "disabled" : "default"
    );

    const setUpButtonState = computed(() => {
      if (isPlanning.value || isFinishing.value) return "disabled" as const;
      if (organizationSetupOperations.value.length === 0)
        return "disabled" as const;
      return "default" as const;
    });

    const getDefaultWorkspaceWithRetry = async (
      orgId: string
    ): Promise<WorkspaceId | undefined> => {
      for (let i = 0; i < 15; i++) {
        const wsId = await getOrganizationDefaultWorkspace(controller, orgId);
        if (wsId) return wsId;
        await new Promise<void>((r) => window.setTimeout(r, 200));
      }
      return undefined;
    };

    const togglePrimaryUseCase = (key: PrimaryUseCaseKey) => {
      const i = primaryUseCaseKeys.value.indexOf(key);
      if (i >= 0) {
        primaryUseCaseKeys.value = primaryUseCaseKeys.value.filter(
          (k) => k !== key
        );
      } else {
        primaryUseCaseKeys.value = [...primaryUseCaseKeys.value, key];
      }
    };

    const runCreation = async (_close: () => void) => {
      const name = trimmedNameForDisplay.value;
      if (!name || isBusy.value) return;

      errorMessage.value = null;
      wizardStep.value = 2;
      isBusy.value = true;

      const started = Date.now();

      try {
        const orgId = await createOrganization(controller, baseLogger, name, {
          initializeDefaultWorkspace: false,
        });
        const defaultWorkspaceId = await getDefaultWorkspaceWithRetry(orgId);
        if (!defaultWorkspaceId) {
          errorMessage.value =
            "Failed to find the default workspace for this organization.";
          return;
        }

        // The built-in Tasks/Pages/Projects screens rely on the provisioned
        // Task/Page/Project types: create them right away. The wizard only
        // decides which screens end up pinned; it installs nothing for the
        // built-in use cases. The workspace home is deliberately left unset
        // so a starter package can still provide it.
        await ensureProvisionedDatabases(
          controller,
          profile.value,
          defaultWorkspaceId
        );

        createdOrgId.value = orgId;
        createdWorkspaceId.value = defaultWorkspaceId;

        const elapsed = Date.now() - started;
        let remaining = Math.max(0, CREATION_MIN_DISPLAY_MS - elapsed);
        while (remaining > 0) {
          const chunk = Math.min(50, remaining);
          await new Promise<void>((r) => setTimeout(r, chunk));
          remaining -= chunk;
        }

        wizardStep.value = 3;
      } catch (err) {
        errorMessage.value =
          err instanceof Error ? err.message : "Something went wrong.";
      } finally {
        isBusy.value = false;
      }
    };

    const onCreate = (close: () => void) => {
      void runCreation(close);
    };

    /** After a plan group executes, mark every checklist step of that group complete. */
    const revealPlanStepsForGroupIndex = (groupIndex: number): void => {
      const ends = planGroupEndIndices.value;
      const endIdx = ends[groupIndex];
      if (endIdx == null) return;
      const prevEnd = groupIndex > 0 ? ends[groupIndex - 1] : undefined;
      const startIdx = (prevEnd ?? -1) + 1;
      if (startIdx > endIdx) return;
      lastCompletedPlanStepIndex.value = endIdx;
    };

    /**
     * The built-in use cases (Projects, Knowledge) require no setup: their
     * screens ship with every organization. Deselecting one in the wizard
     * unpins its screen from the menu.
     */
    const unpinDeselectedBuiltinScreens = async (orgId: OrgId) => {
      for (const definition of PRIMARY_USE_CASE_DEFINITIONS) {
        if (!definition.screenId) continue;
        if (primaryUseCaseKeys.value.includes(definition.key)) continue;
        await menu.unpinAppScreen(definition.screenId, orgId);
      }
    };

    const beginPlanPhase = async (_close: () => void) => {
      if (
        wizardStep.value !== 3 ||
        primaryUseCaseKeys.value.length === 0 ||
        isPlanning.value
      ) {
        return;
      }
      if (!createdOrgId.value || !createdWorkspaceId.value) return;

      wizardStep.value = 4;
      purposeError.value = null;
      isPlanning.value = true;

      try {
        const sortedUseCases = [...new Set(primaryUseCaseKeys.value)].sort();
        // Built-in use cases install nothing: they only drive menu pinning.
        const packagedUseCases = sortedUseCases.filter(
          (key) => !screenIdForUseCase(key)
        );
        const nextPlannedPackages: PlannedPackage[] = [];
        for (const key of packagedUseCases) {
          const entryId = onboardingExtensionId(key);
          if (!entryId) continue;
          const pkg = await loadPackage(entryId);
          if (pkg) {
            nextPlannedPackages.push({ key, pkg });
          }
        }
        if (packagedUseCases.length > 0 && nextPlannedPackages.length === 0) {
          purposeError.value =
            "No starter template is published for your selection yet. Use Skip to set up manually.";
          wizardStep.value = 3;
          return;
        }

        // One plan group per selected use case, in the execution order.
        // Built-in ones show their single (pinning) step.
        const nextGroups: OrganizationPlanGroup[] = [];
        const nextGroupEndIndices: number[] = [];
        for (const key of sortedUseCases) {
          const definition = PRIMARY_USE_CASE_DEFINITIONS.find(
            (x) => x.key === key
          );
          if (!definition) continue;
          let operations: string[];
          if (definition.screenId) {
            const label =
              ORGANIZATION_APP_SCREEN_LABELS[definition.screenId] ??
              definition.title;
            operations = [`Pin the ${label} screen to the menu`];
          } else {
            const planned = nextPlannedPackages.find((p) => p.key === key);
            if (!planned) continue;
            operations = buildOperationsFromPackage(planned.pkg);
          }
          nextGroups.push({ key, title: definition.title, operations });
          const flatSoFar = nextGroups.reduce(
            (sum, g) => sum + g.operations.length,
            0
          );
          nextGroupEndIndices.push(flatSoFar - 1);
        }
        const flatCount = nextGroups.reduce(
          (sum, g) => sum + g.operations.length,
          0
        );
        if (flatCount === 0) {
          purposeError.value =
            "Starter templates are empty. Use Skip to set up manually.";
          wizardStep.value = 3;
          return;
        }
        plannedPackages.value = nextPlannedPackages;
        organizationPlanGroups.value = nextGroups;
        // Only auto-expand when a single module was selected; with multiple
        // modules keep them all collapsed so the summary stays compact.
        planGroupExpanded.value = nextGroups.map(() => nextGroups.length === 1);
        planGroupEndIndices.value = nextGroupEndIndices;
        lastCompletedPlanStepIndex.value = -1;
        wizardStep.value = 5;
      } catch (err) {
        purposeError.value =
          err instanceof Error ? err.message : "Something went wrong.";
        wizardStep.value = 3;
      } finally {
        isPlanning.value = false;
      }
    };

    const onExecutePlannedSetup = async (_close: () => void) => {
      const orgId = createdOrgId.value;
      const wsId = createdWorkspaceId.value;
      if (
        !orgId ||
        !wsId ||
        isFinishing.value ||
        isPlanning.value ||
        organizationPlanGroups.value.length === 0
      )
        return;

      purposeError.value = null;
      wizardStep.value = 6;
      isFinishing.value = true;
      lastCompletedPlanStepIndex.value = -1;

      try {
        const groups = organizationPlanGroups.value;
        for (let i = 0; i < groups.length; i += 1) {
          installingPlanGroupIndex.value = i;
          const group = groups[i];

          // Built-in use cases: nothing to install, just pin the screen.
          const screenId = screenIdForUseCase(group.key);
          if (screenId) {
            await menu.pinAppScreen(screenId, orgId);
            revealPlanStepsForGroupIndex(i);
            continue;
          }

          const planned = plannedPackages.value.find(
            (p) => p.key === group.key
          );
          if (!planned) continue;
          const workspaceDict = await workspaceDbDictForInstall(wsId);
          const { modeByDbId, targetByDbId } = buildOnboardingInstallMaps(
            planned.pkg,
            workspaceDict
          );
          const importResult = await importPackage(
            controller,
            baseLogger,
            wsId,
            planned.pkg,
            workspaceDict,
            modeByDbId,
            targetByDbId,
            undefined,
            undefined,
            orgId
          );
          // Seed the packaged view display preferences before the imported
          // blocks first render, keyed by the fresh block ids the import
          // generated (see view-configurations.ts).
          applyViewConfigurations(
            browserStore,
            planned.pkg,
            importResult.blockIdMap
          );

          if (importResult.rootObject) {
            const root = importResult.rootObject;
            const rootFullId = String(
              await controller.graph.object.id.make({
                workspace_id: wsId,
                table_id: root.databaseId,
                row_id: root.id,
              })
            );
            await menu.pinObject(rootFullId, "object");
          }

          revealPlanStepsForGroupIndex(i);
        }

        installingPlanGroupIndex.value = null;

        await unpinDeselectedBuiltinScreens(orgId);

        // The workspace always gets a (unpinned) Home page, whatever was
        // installed; an existing home (e.g. when the organization was
        // created during onboarding) is reused as-is.
        if (!workspaceInit.value) {
          const ensured = await ensureWorkspaceHome(controller, wsId);
          if (!ensured) {
            throw new Error(
              "Failed to set up the default workspace home page."
            );
          }
          workspaceInit.value = ensured;
        }

        wizardStep.value = 7;
      } catch (err) {
        purposeError.value =
          err instanceof Error ? err.message : "Something went wrong.";
        wizardStep.value = 5;
      } finally {
        installingPlanGroupIndex.value = null;
        isFinishing.value = false;
      }
    };

    const isPlanStepCompleted = (stepIndex: number): boolean =>
      lastCompletedPlanStepIndex.value >= stepIndex;

    const isPlanUseCaseLoading = (groupIndex: number): boolean =>
      wizardStep.value === 6 &&
      isFinishing.value &&
      installingPlanGroupIndex.value === groupIndex;

    const globalPlanOpIndex = (
      groupIndex: number,
      localIndex: number
    ): number => {
      const groups = organizationPlanGroups.value;
      let offset = 0;
      for (let i = 0; i < groupIndex; i++) {
        offset += groups[i]?.operations.length ?? 0;
      }
      return offset + localIndex;
    };

    /** True once every checklist step for this use case has completed (flat indices are sequential). */
    const isPlanUseCaseComplete = (groupIndex: number): boolean => {
      const groups = organizationPlanGroups.value;
      const group = groups[groupIndex];
      if (!group || group.operations.length === 0) return false;
      const lastFlat = globalPlanOpIndex(
        groupIndex,
        group.operations.length - 1
      );
      return isPlanStepCompleted(lastFlat);
    };

    const togglePlanGroup = (groupIndex: number) => {
      const cur = [...planGroupExpanded.value];
      while (cur.length <= groupIndex) cur.push(true);
      cur[groupIndex] = !(cur[groupIndex] ?? true);
      planGroupExpanded.value = cur;
    };

    const onEditPlanWithAgent = async (close: () => void) => {
      const orgId = createdOrgId.value;
      if (!orgId || isPlanning.value || isFinishing.value) return;
      if (organizationPlanGroups.value.length === 0) return;

      purposeError.value = null;

      try {
        const personalOrgId = profile.value?.user.personal.organizationId;
        if (!personalOrgId) {
          throw new Error("Assistant is not available.");
        }

        const assistantRef = await controller.ai.assistant.get();
        const assistantId = assistantRef.value;
        assistantRef.release();
        if (!assistantId) {
          throw new Error("Assistant is not available.");
        }

        const convId = await controller.ai.agent.conversation.create(
          personalOrgId,
          assistantId,
          null
        );
        const inline = formatPrimaryUseCasesInline(primaryUseCaseKeys.value);
        const planByUseCase = formatPlanByUseCaseForAssist(
          organizationPlanGroups.value
        );
        const orgName =
          trimmedNameForDisplay.value ||
          (await controller.organization.get(orgId)).release()?.name ||
          "Organization";

        const assistText = `You are operating on behalf of the user to set up the organization ${orgName} (${orgId}).

I would like to set up my organization with help editing this plan. My primary use cases are: ${inline}.

Proposed plan by use case:

${planByUseCase}

I'd like to adjust this - please help me refine and execute the setup.`;

        const assistDoc = schema.nodes.doc.create(null, [
          schema.nodes.paragraph.create(null, schema.text(assistText)),
        ]);
        pendingMessage.value = {
          conversationId: convId,
          doc: assistDoc,
          text: serializeDoc(assistDoc),
          sendImmediately: false,
        };
        close();
        await navigateToAgentConversation(router, assistantId, convId);
      } catch (err) {
        purposeError.value =
          err instanceof Error ? err.message : "Something went wrong.";
      }
    };

    const onOpenOrganization = async (close: () => void) => {
      const init = workspaceInit.value;
      const orgId = createdOrgId.value;
      if (!init || !orgId) return;
      // Open the Home page through the organization's Pages screen, so the
      // user lands on the pages list with Home selected.
      const parsed = await controller.graph.object.id.parse(init.homeObjectId);
      close();
      await router.push({
        name: RouteNames.OrgPages,
        params: { orgId },
        query: { id: parsed.row_id },
      });
    };

    const onSkipManualSetup = async (close: () => void) => {
      if (isPlanning.value || isFinishing.value) return;

      let init = workspaceInit.value;
      const orgId = createdOrgId.value;
      const wsId = createdWorkspaceId.value;
      if (!orgId || !wsId) return;

      if (!init) {
        purposeError.value = null;
        // Reuses the workspace's existing home when it was already
        // initialized (e.g. the organization was created during onboarding),
        // and otherwise creates one, reusing an existing Page type when
        // present.
        const ensured = await ensureWorkspaceHome(controller, wsId);
        if (!ensured) {
          purposeError.value =
            "Failed to set up the default workspace home page.";
          return;
        }
        init = ensured;
        workspaceInit.value = ensured;
      }

      close();
      await navigateToObject(router, controller, {
        kind: "object",
        id: init.homeObjectId,
      });
    };

    const onHeaderClose = (close: () => void) => {
      if (wizardStep.value >= 3 && wizardStep.value <= 6) {
        void onSkipManualSetup(close);
      } else {
        close();
      }
    };

    const onContinuePurposeStep = (close: () => void) => {
      if (wizardStep.value !== 3) return;
      if (purposeContinueState.value === "disabled") return;
      void beginPlanPhase(close);
    };

    const onBackPurposeStep = (_close: () => void) => {
      if (isFinishing.value || isPlanning.value) return;
      if (wizardStep.value !== 5) return;
      wizardStep.value = 3;
      purposeError.value = null;
      lastCompletedPlanStepIndex.value = -1;
      plannedPackages.value = [];
      planGroupEndIndices.value = [];
      organizationPlanGroups.value = [];
      planGroupExpanded.value = [];
      installingPlanGroupIndex.value = null;
    };

    const onKeydown = (event: KeyboardEvent, close: () => void) => {
      if (event.key === "Enter" && createButtonState.value === "default") {
        onCreate(close);
      }
    };

    const focusNameInput = async () => {
      if (wizardStep.value !== 1 || isBusy.value) return;
      await nextTick();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          inputRef.value?.focus();
        });
      });
    };

    const handleModalClosed = () => {
      props.afterClosed?.();
    };

    const initExistingOrganization = async (orgId: OrgId) => {
      existingOrgBootstrapPending.value = true;
      errorMessage.value = null;
      try {
        const wsId = await getDefaultWorkspaceWithRetry(orgId);
        if (!wsId) {
          errorMessage.value =
            "Could not load this organization's workspace. You can set up the organization from Settings.";
          return;
        }
        const orgRef = await controller.organization.get(orgId);
        const org = orgRef.release();
        organizationName.value = org?.name ?? "";
        createdOrgId.value = orgId;
        createdWorkspaceId.value = wsId;
        phase.value = "purpose";
        wizardStep.value = 3;
      } catch (e) {
        errorMessage.value =
          e instanceof Error ? e.message : "Something went wrong.";
      } finally {
        existingOrgBootstrapPending.value = false;
      }
    };

    /**
     * Props from `useModal({ attrs })` may not exist on first tick — watch so programmatic opens
     * (e.g. post-setup queue) still bootstrap to the setup intro (step 3) instead of the name step.
     */
    watch(
      () => props.existingOrganizationId,
      (orgId) => {
        if (!orgId) return;
        skipNameStepAutoFocus.value = true;
        void initExistingOrganization(orgId);
      },
      { immediate: true }
    );

    let removeGuard: (() => void) | undefined;
    onMounted(() => {
      removeGuard = router.beforeEach(() => {
        if ([2, 4, 6].includes(wizardStep.value)) return false;
        if (isBusy.value || isPlanning.value || isFinishing.value) return false;
        return true;
      });
      void nextTick(() => {
        if (!skipNameStepAutoFocus.value) {
          void focusNameInput();
        }
      });
    });
    onBeforeUnmount(() => {
      removeGuard?.();
    });

    watch(phase, (newPhase) => {
      if (newPhase === "name") {
        wizardStep.value = 1;
        purposeError.value = null;
        primaryUseCaseKeys.value = [...DEFAULT_PRIMARY_USE_CASE_KEYS];
        workspaceInit.value = null;
        isPlanning.value = false;
        isFinishing.value = false;
        plannedPackages.value = [];
        planGroupEndIndices.value = [];
        organizationPlanGroups.value = [];
        planGroupExpanded.value = [];
        lastCompletedPlanStepIndex.value = -1;
        installingPlanGroupIndex.value = null;
        void focusNameInput();
      }
    });

    return {
      modalFocusTrap,
      theme,
      phase,
      wizardStep,
      userFacingStep,
      USER_FACING_WIZARD_STEPS,
      modalAllowsDismiss,
      showHeaderClose,
      title,
      organizationName,
      trimmedNameForDisplay,
      inputRef,
      isBusy,
      errorMessage,
      createButtonState,
      createButtonText,
      primaryUseCases,
      primaryUseCaseKeys,
      purposeError,
      purposeContinueState,
      purposeContinueLabel,
      purposeFooterBackState,
      isFinishing,
      isPlanning,
      planActionButtonState,
      setUpButtonState,
      togglePrimaryUseCase,
      onCreate,
      onKeydown,
      onContinuePurposeStep,
      onBackPurposeStep,
      onHeaderClose,
      onExecutePlannedSetup,
      onOpenOrganization,
      onSkipManualSetup,
      onEditPlanWithAgent,
      isPlanStepCompleted,
      isPlanUseCaseLoading,
      organizationPlanGroups,
      planGroupExpanded,
      organizationSetupOperations,
      globalPlanOpIndex,
      isPlanUseCaseComplete,
      togglePlanGroup,
      handleModalClosed,
      existingOrgBootstrapPending,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/_variables.scss" as *;

.create-org-modal {
  &--wide {
    min-width: 600px;
  }

  &--creating .body {
    min-height: 240px;
    display: flex;
    flex-direction: column;
  }
}

.title .text {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.step-indicator {
  @extend %font-caption-regular;

  @include themed() {
    color: t($grey-50);
  }
}

.creating-loader {
  display: flex;
  align-items: center;
  justify-content: center;
}

.creating-spinner {
  display: block;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 2px solid;
  border-top-color: transparent;
  animation: spin 0.85s linear infinite;

  @include themed() {
    border-color: t($grey-30);
    border-top-color: transparent;
  }
}

.creating-error {
  margin-top: 16px;
  text-align: center;
  max-width: 100%;
  flex-shrink: 0;
}

.error {
  margin-top: 12px;

  @include themed() {
    color: t($secondary-50);
  }
}

.purpose-body {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.setup-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.setup-question-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.setup-question-hint {
  @include themed() {
    color: t($grey-40) !important;
  }
}

.setup-error {
  margin-top: 0;
}

.use-case-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  &--multiline {
    flex-direction: column;
    align-items: stretch;
  }
}

.use-case-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: $border-radius-m;
  border: 1px solid;
  background: none;
  cursor: default;
  @extend %font-caption-medium;
  @include transition(all 100ms ease-out);

  @include themed() {
    border-color: t($grey-15);
    color: t($grey-60);
  }

  &:hover {
    @include themed() {
      border-color: t($grey-30);
      color: t($primary);
    }
  }

  &--multiline {
    align-items: flex-start;
    flex-direction: row;
    justify-content: flex-start;
    text-align: start;
    padding: 10px 14px;
    gap: 10px;
  }

  &--selected {
    @include themed() {
      border-color: t($grey-50);
      background: t($grey-05);
    }
  }
}

.use-case-pill-icon {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 1px;

  @include themed() {
    color: t($grey-60);
  }

  :deep(svg) {
    width: 18px;
    height: 18px;
    display: block;
  }

  :deep(.stroke) {
    @include themed() {
      stroke: t($grey-40);
    }
  }
}

.use-case-pill-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: start;
  flex: 1;
  min-width: 0;
  gap: 4px;
}

.use-case-pill-title {
  @extend %font-caption-medium;
  text-align: start;
  width: 100%;
  @include themed() {
    color: t($primary);
  }
}

.use-case-pill-hint {
  @extend %font-caption-regular;
  text-align: start;
  width: 100%;
  @include themed() {
    color: t($grey-40);
  }
}

.plan-phase-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px 0;

  &--standalone {
    padding: 32px 0 8px;
  }

  /* Only `gap` should separate icon and text — cancel the hint's own margins. */
  .plan-hint {
    margin-top: 0;
    margin-bottom: 0;
  }
}

/* Isolate the spinning border so it doesn't composite/blur sibling label text (Chrome/WebKit). */
.plan-phase-loading-spin-host {
  contain: layout paint;
  isolation: isolate;
}

.plan-phase-loading-status {
  position: relative;
  z-index: 1;
  transform: translateZ(0);
}

.plan-preparing-hint {
  margin-top: 0;
  margin-bottom: 0;
  text-align: center;
}

.plan-phase-loader {
  min-height: 48px;
}

.complete-section {
  padding: 8px 0 16px;
}

.complete-title {
  margin-bottom: 8px;
}

.complete-hint {
  margin: 0;
}

.complete-footer {
  justify-content: flex-end;

  // The shared modal footer style (`.final-modal … .footer .buttons`) forces
  // `justify-content: space-between` and outranks the scoped rule above, which
  // left-aligns the lone "Open" button. Push it right the same way the plan
  // footer does its actions.
  :deep(.base-button) {
    margin-inline-start: auto;
  }
}

.plan-header {
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.plan-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
}

.plan-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;

  @include themed() {
    color: t($grey-60);
  }

  :deep(svg) {
    width: 22px;
    height: 22px;
    display: block;
  }

  :deep(.stroke) {
    @include themed() {
      stroke: currentColor;
    }
  }
}

.plan-edit-btn {
  flex-shrink: 0;
  margin-inline-start: auto;
}

.plan-hint {
  margin-top: 8px;
  margin-bottom: 14px;
}

.plan-uc {
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.plan-uc-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 4px 4px;
  border: none;
  text-align: start;
  border-radius: $border-radius-m;
  @extend %font-body-regular;
  @include transition(background-color 100ms ease-out);

  @include themed() {
    color: t($primary);
  }

  &:hover {
    @include themed() {
      color: t($primary);
      background: t($grey-05);
    }
  }
}

.plan-uc-heading {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.plan-uc-title {
  min-width: 0;
}

.plan-uc-done {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid;
  box-sizing: border-box;

  @include themed() {
    border-color: t($tertiary-50);
    color: t($tertiary-50);
  }
}

.plan-uc-done-icon {
  display: block;
}

.plan-uc-status--loading {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.plan-uc-spinner {
  display: block;
  width: 11px;
  height: 11px;
  border-radius: 999px;
  border: 2px solid;
  border-top-color: transparent;
  box-sizing: border-box;
  animation: spin 0.85s linear infinite;

  @include themed() {
    border-color: t($grey-40);
    border-top-color: transparent;
  }
}

.plan-uc-chevron {
  flex-shrink: 0;
  width: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  @include transition(transform 150ms ease);

  @include themed() {
    color: t($grey-60);
  }

  &--open {
    transform: rotate(90deg);
  }
}

.plan-checklist {
  list-style: none;
  margin: 0;
  padding: 0;

  &--nested {
    margin-top: 6px;
    margin-bottom: 2px;
    padding-inline-start: 28px;
  }
}

.plan-checklist-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  @extend %font-caption-regular;

  &:last-child {
    margin-bottom: 0;
  }
}

.plan-check {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;

  :deep(svg) {
    width: 10px;
    height: 10px;
    display: block;
  }

  :deep(.stroke) {
    @include themed() {
      stroke: t($primary);
    }
  }

  &--done :deep(.stroke) {
    @include themed() {
      stroke: t($tertiary-50);
    }
  }
}

.plan-check-text {
  flex: 1;
  min-width: 0;
  @extend %font-caption-regular;
  line-height: 1.45;
  @include transition(color 150ms ease, text-decoration-color 150ms ease);

  @include themed() {
    color: t($grey-70);
  }

  &--done {
    text-decoration: line-through;

    @include themed() {
      color: t($grey-50);
    }
  }
}

.footer-btns {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  column-gap: 8px;
}

.plan-footer {
  justify-content: space-between;
  align-items: center;
}

.plan-footer-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-inline-start: auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>

<!-- Global modal `.body` reserves 70px bottom padding for footers; this phase has no footer. -->
<style lang="scss">
.final-modal .vfm__content > .content.create-org-modal--creating > .body {
  padding-bottom: 24px;
}
</style>
